/**
 * 02 · What rcrt is — the CEO concept's four layers (Connect / Orchestrate /
 * Govern / Run), copy truth-adjusted to the real platform: breadcrumbs, LOD
 * memory, sandboxed Deno tools, multi-tenant isolation, BYO models via
 * route/provider/model (no "200+ models" claim).
 */
export default function WhatRcrt() {
  return (
    <section id="how">
      <div className="wrap">
        <div className="sec-head">
          <div className="sec-num">02 · What rcrt is</div>
          <div>
            <h2>
              Not another app. The <span className="ph">runtime</span> underneath them all.
            </h2>
            <p className="lede">
              rcrt is infrastructure: an agent and context runtime that executes complex,
              multi-step work across your systems with governance, security and repeatability
              built in. Everything in it is a breadcrumb — one universal, tagged, searchable
              unit of data. Tools become agents. Agents run on one engine you control.
            </p>
          </div>
        </div>
        <div className="layers">
          <div className="layer">
            <span className="k">01 · Connect</span>
            <h4>Your systems, one context</h4>
            <p>
              Every record, action and document becomes a breadcrumb: tagged, versioned,
              embedded and searchable, joined up across what used to be silos. 18 first-party
              connectors today — and a new one is a definition you write in minutes, not a
              vendor integration you wait for.
            </p>
          </div>
          <div className="layer">
            <span className="k">02 · Orchestrate</span>
            <h4>Agents and people, together</h4>
            <p>
              Agents with Level-of-Detail memory run the work your SaaS tools used to do —
              context assembled in 50–200ms per turn — with humans approving every action
              where judgement matters.
            </p>
          </div>
          <div className="layer">
            <span className="k">03 · Govern</span>
            <h4>Audited by default</h4>
            <p>
              Row-level tenant isolation, role-based access, sandboxed tool execution with
              per-tool permissions, and a trace on every context assembly. Sovereign
              deployment on your own cloud project.
            </p>
          </div>
          <div className="layer">
            <span className="k">04 · Run</span>
            <h4>Production, not pilots</h4>
            <p>
              Model-agnostic across 300+ models via one address — Vertex AI, Anthropic,
              OpenAI, Google and the full OpenRouter catalogue — switchable per agent,
              fail-loud, no silent fallbacks. Priced on what you run, not seats you forgot
              you bought.
            </p>
          </div>
        </div>

        <div className="space-block">
          <video
            className="space-video"
            src="/breadcrumb-space.mp4"
            poster="/breadcrumb-space-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="space-caption">
            <span className="eyebrow ph">Real footage · a live workspace</span>
            <p>
              This is not a diagram. Every point is a real breadcrumb in a live rcrt
              workspace — 475 of them here: 49 tools, 11 agents, 33 knowledge docs, 24
              services and 358 messages — positioned by their embeddings in semantic space.
              Agents navigate this space every turn; the LOD engine renders what's near in
              full detail and compresses the rest.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
